/**
 * NOS-154: Global Cmd+Z / Shift+Cmd+Z (Ctrl on non-Mac) undo/redo shortcuts.
 *
 * Does NOT fire while focus is inside an input, textarea, select, or
 * contenteditable region (TipTap) — native text undo keeps working there.
 */
import { useEffect } from "react"

/** True when the element is a text-entry target whose native undo must win. */
export function isEditableTarget(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
  if (el instanceof HTMLElement && el.isContentEditable) return true
  // Fallback for environments/nodes where isContentEditable isn't reflected:
  // treat anything inside a contenteditable region (e.g. TipTap) as editable.
  return !!el.closest?.('[contenteditable=""], [contenteditable="true"]')
}

export function useUndoRedoShortcuts(
  undo: () => void | Promise<void>,
  redo: () => void | Promise<void>
) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return
      if (e.key.toLowerCase() !== "z") return
      if (isEditableTarget(document.activeElement)) return
      if (e.target instanceof Element && isEditableTarget(e.target)) return
      e.preventDefault()
      if (e.shiftKey) void redo()
      else void undo()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [undo, redo])
}
